import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, RotateCcw, Save } from "lucide-react";
import api from "@/lib/api";
import { NAV_GROUPS, ROLES, ROLE_LABELS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

const ROLE_COLS = ROLES.filter((r) => r !== "sc");

export function MenuManager() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["menu-visibility"],
    queryFn: async () => (await api.get("/menu-visibility")).data,
  });
  const [vis, setVis] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!data) return;
    const ov = data.overrides || {};
    const init = {};
    NAV_GROUPS.forEach((g) => g.items.forEach((i) => { init[i.to] = new Set(ov[i.to] || i.roles); }));
    setVis(init);
  }, [data]);

  if (isLoading || !vis) return <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;

  const toggle = (to, role) => {
    setVis((v) => {
      const next = new Set(v[to]);
      if (next.has(role)) next.delete(role); else next.add(role);
      next.add("super_admin");
      return { ...v, [to]: next };
    });
  };

  const save = async (overrides) => {
    setSaving(true);
    try {
      await api.put("/menu-visibility", { overrides });
      qc.invalidateQueries({ queryKey: ["menu-visibility"] });
      toast.success("Menu visibility saved — users see it on their next page load");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="menu-manager">
      <p className="text-xs text-slate-500 mb-3">Tick a box to show that menu item to that role. This only hides menu entries — it does not change data permissions. Super Admin always sees everything.</p>
      <div className="overflow-x-auto border border-slate-200 rounded-md">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50">
              <th className="text-left px-2 py-2 font-semibold text-slate-500 uppercase tracking-wide sticky left-0 bg-slate-50 min-w-[160px]">Menu item</th>
              {ROLE_COLS.map((r) => (
                <th key={r} className="px-1.5 py-2 font-semibold text-slate-500 whitespace-nowrap" title={ROLE_LABELS[r]}>
                  {ROLE_LABELS[r].split(" ").map((w) => w[0]).join("")}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {NAV_GROUPS.map((g) => (
              [<tr key={g.key} className="bg-slate-100/70">
                <td colSpan={ROLE_COLS.length + 1} className="px-2 py-1.5 font-bold text-slate-600 uppercase tracking-wider text-[10px] sticky left-0">{g.label}</td>
              </tr>,
              ...g.items.map((i) => (
                <tr key={i.to} className="border-t border-slate-100" data-testid={`menu-row-${i.to.replace(/\//g, "_")}`}>
                  <td className="px-2 py-1.5 text-slate-700 font-medium whitespace-nowrap sticky left-0 bg-white">{i.label}</td>
                  {ROLE_COLS.map((r) => (
                    <td key={r} className="px-1.5 py-1.5 text-center">
                      <Checkbox checked={vis[i.to]?.has(r) || false} disabled={r === "super_admin"}
                        onCheckedChange={() => toggle(i.to, r)}
                        data-testid={`menu-chk-${i.to.replace(/\//g, "_")}-${r}`} />
                    </td>
                  ))}
                </tr>
              ))]
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-slate-400 mt-2">Columns: {ROLE_COLS.map((r) => `${ROLE_LABELS[r].split(" ").map((w) => w[0]).join("")} = ${ROLE_LABELS[r]}`).join(" · ")}</p>
      <div className="flex gap-2 mt-3">
        <Button size="sm" className="gap-1.5 bg-slate-900 hover:bg-slate-800" disabled={saving}
          onClick={() => save(Object.fromEntries(Object.entries(vis).map(([to, set]) => [to, [...set]])))}
          data-testid="menu-manager-save">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
        </Button>
        <Button size="sm" variant="outline" className="gap-1.5" disabled={saving}
          onClick={() => save({})} data-testid="menu-manager-reset">
          <RotateCcw className="h-3.5 w-3.5" /> Reset to defaults
        </Button>
      </div>
    </div>
  );
}
